import {
  type Product,
  getTranslatedProductDescription,
  getTranslatedProductName,
} from "@/app/directories/shop/helper";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@frontend/shared/components/ui/accordion";
import { Separator } from "@frontend/shared/components/ui/separator";
import { CategoryGrid } from "./CategoryGrid";
import { CategoryTreatmentWrapper } from "./CategoryTreatmentWrapper";

interface CategorySectionProps {
  category: {
    id: string;
    name: string;
  };
  products: Product[];
  clientSlug: string;
  orgId: number;
  languageCode: string;
  displayMode?: "accordion" | "grid" | "default";
  defaultChecked?: boolean;
  priceToggleColor?: string;
}

export function CategorySection({
  category,
  products,
  clientSlug,
  orgId,
  languageCode,
  displayMode = "default",
  defaultChecked = false,
  priceToggleColor = "#FFFFFF",
}: CategorySectionProps) {
  if (products.length === 0) return null;

  const sortedProductsByPopularity = products.sort(
    (a, b) => b.popularity - a.popularity,
  );

  if (displayMode === "grid") {
    return (
      <CategoryGrid
        sortedProductsByPopularity={sortedProductsByPopularity}
        clientSlug={clientSlug}
        priceToggleColor={priceToggleColor}
        category={category}
        orgId={orgId}
        languageCode={languageCode}
      />
    );
  }

  if (displayMode === "accordion") {
    return (
      <div className="flex flex-col">
        <Separator className="h-[2px]" />
        <div className="mx-4 lg:mx-16 py-6 lg:py-8">
          <Accordion
            type="single"
            className="w-full"
            collapsible
            defaultValue={defaultChecked ? category.id : undefined}
          >
            <AccordionItem
              value={category.id}
              className="flex flex-row items-start"
            >
              <AccordionTrigger
                isAbsoluteTrigger={false}
                className="config-h3 font-bold border-none text-left justify-start gap-8 whitespace-nowrap"
              >
                {category.name.toUpperCase()}
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-10 items-end">
                  {sortedProductsByPopularity.map((product, index) => (
                    <div
                      key={`category-section-${product.id}`}
                      className="w-5/6"
                    >
                      <CategoryTreatmentWrapper
                        product={product}
                        hrts={product.productToHrt}
                        description={getTranslatedProductDescription(
                          product,
                          languageCode,
                        )}
                        name={getTranslatedProductName(product, languageCode)}
                        productId={product.id.toString()}
                        clientSlug={clientSlug}
                        priceToggleColor={priceToggleColor}
                        displayMode={"default"}
                        orgId={orgId}
                      />
                      {index < products.length - 1 && (
                        <Separator className="mt-10" />
                      )}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <Separator />
      <div className="flex flex-col lg:flex-row lg:gap-10 sm:gap-4 mx-4 lg:mx-16 py-6 lg:py-12 ">
        <h1 className="text-4xl config-h3  font-bold w-full lg:w-1/3 mb-8 lg:mb-0">
          {category.name}
        </h1>
        <div className="flex flex-col gap-10 w-full lg:w-2/3">
          {sortedProductsByPopularity.map((product, index) => {
            return (
              <div key={`category-section ${product.id}`} className="w-full">
                <CategoryTreatmentWrapper
                  product={product}
                  hrts={product.productToHrt.filter(
                    (hrt) => !!hrt.hrt.isConsumerFacing,
                  )}
                  description={getTranslatedProductDescription(
                    product,
                    languageCode,
                  )}
                  name={getTranslatedProductName(product, languageCode)}
                  productId={product.id.toString()}
                  clientSlug={clientSlug}
                  priceToggleColor={priceToggleColor}
                  displayMode={"default"}
                  orgId={orgId}
                />
                {index < products.length - 1 && <Separator />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
